export const cacheBucket = "Profinda-";
export const cacheSuffix = "-cacheexpiration";

const expiryRadix = 10;
const expiryUnits = 1000; // time is stored in seconds
const quotaErrors = ["QUOTA_EXCEEDED_ERR", "NS_ERROR_DOM_QUOTA_REACHED", "QuotaExceededError"];

export type StorageOptions = {
  expiresIn?: number;
  preserveTime?: boolean;
};

export default abstract class Storage {
  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  abstract setItem(key: string, value: any, options?: StorageOptions): void | boolean;
  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  abstract getItem(key: string): any;
  abstract removeItem(key: string): void;
  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  abstract clear(): void;
  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  abstract key(index: number): string | null | undefined;
  abstract eachKey(callback: (key: string) => void): void;
  abstract expirationTime(key: string): number | null;

  // fallow-ignore-next-line unused-class-member -- called polymorphically via window.storage: Storage
  length(): number {
    let count = 0;
    this.eachKey(() => (count = count + 1));
    return count;
  }

  flushExpired(): void {
    this.eachKey((key) => this.flushExpiredItem(key));
  }

  flushExpiredItem(key: string): boolean | undefined {
    if (this.isExpired(key)) {
      this.removeItem(key);
      return true;
    }
  }

  isExpired(key: string): boolean {
    const expTime = this.expirationTime(key);
    if (expTime) {
      return this.currentTime() >= expTime;
    } else {
      return false;
    }
  }

  currentTime(): number {
    return Math.floor(new Date().getTime() / expiryUnits);
  }

  storageKey(key: string): string {
    return `${cacheBucket}${key}`;
  }

  expirationKey(key: string): string {
    return `${cacheBucket}${key}${cacheSuffix}`;
  }

  expirationTimeFor(value: number): string {
    return (this.currentTime() + value).toString(expiryRadix);
  }

  isOutOfSpace(error: Error | null | undefined): boolean {
    return !!(error && quotaErrors.indexOf(error.name) >= 0);
  }

  stringify(value: unknown): string | null {
    if (typeof value === "string") {
      return value;
    } else {
      try {
        return JSON.stringify(value);
      } catch {
        return null;
      }
    }
  }
}
